import { Alert, Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { useIsMobile } from "../../hook/useIsMobile";
import MultipleSelect from "../select/multipleSelect.component";
import { useEffect, useMemo, useState } from "react";
import TimeRangeButtons from "../buttons/timeRangeButtons.component";
import SensorAverageChart from "../chart/sensorAverageChart.component";
import { useSelector } from "react-redux";
import LoadingChartState from "../chart/loadingChartState.component";
import { getMeasureParameters } from "../../utils/chartUtils";
import {
  selectFormattedAverageData,
  selectSensorAverageState,
} from "../../store/selectors/sensorAverageSelectors";
import { useTimeRange } from "../../hook/useTimeRange";
import { useSensorAverageDataFetch } from "../../hook/useSensorAverageDataFetch";
import { getChartErrorMessage } from "../chart/utils";

const EMPTY_MEASURES = [];

const SensorAverageChartPanel = ({
  title,
  subtitle,
  sensors,
  id,
  accessToken,
  scope,
  source = "sensors",
  unavailableMeasures = EMPTY_MEASURES,
}) => {
  const isMobile = useIsMobile();
  const theme = useTheme();

  const [selectedSensors, setSelectedSensors] = useState([]);

  const formattedChartData = useSelector((state) => selectFormattedAverageData(state, scope));
  const { isLoading, error } = useSelector((state) => selectSensorAverageState(state, scope));

  const {
    selectedButton,
    timeRange,
    isTimeRangeReady,
    handleButtonClick,
    handleDateRangeChange,
  } = useTimeRange();

  const availableSelection = useMemo(() => selectedSensors.filter(
    (measure) => !unavailableMeasures.includes(measure),
  ), [selectedSensors, unavailableMeasures]);

  const paramsList = useMemo(() => getMeasureParameters(sensors), [sensors]);

  useEffect(() => {
    const validValues = new Set(paramsList.map((item) => item.value));
    setSelectedSensors((previous) => previous.every((value) => validValues.has(value))
      ? previous : previous.filter((value) => validValues.has(value)));
  }, [paramsList]);

  useSensorAverageDataFetch({
    measures: availableSelection, fieldId: id, accessToken, timeRange, scope, source,
    enabled: isTimeRangeReady && availableSelection.length > 0,
  });

  return (
    <Paper
      sx={{
        p: 3,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
      elevation={0}
    >
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        mb={1}
        gap={2}
      >
        <Box display="flex" flexDirection="row" gap={2}>
          <Stack direction="column">
            <Typography fontWeight="bold" fontSize="14px">
              {title}
            </Typography>
            <Typography
              fontSize="12px"
              color={theme.palette.custom.infoTextColor}
            >
              {subtitle}
            </Typography>
          </Stack>
          <MultipleSelect
            onChange={setSelectedSensors}
            measureList={paramsList}
            value={selectedSensors}
          />
        </Box>
        {selectedSensors.length > 0 ? (
          <TimeRangeButtons
            selectedButton={selectedButton}
            handleButtonClick={handleButtonClick}
            onDateRangeChange={handleDateRangeChange}
          />
        ) : undefined}
      </Box>
      {selectedSensors.some((measure) => unavailableMeasures.includes(measure)) && (
        <Alert severity="warning">
          Medie separate non disponibili per {paramsList
            .filter((item) => selectedSensors.includes(item.value) && unavailableMeasures.includes(item.value))
            .map((item) => item.label).join(", ")}: queste misure sono rilevate sia dai sensori sia dalla stazione meteo.
        </Alert>
      )}
      {error && <Alert severity="error">{getChartErrorMessage(error)}</Alert>}
      {selectedSensors.length === 0 ? (
        <Box
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Seleziona da una a tre misure per visualizzare i dati nel grafico
          </Typography>
        </Box>
      ) : (
        <Box position="relative" width="100%" height="100%">
          {isLoading && <LoadingChartState />}
          <SensorAverageChart
            chartData={formattedChartData}
            selectedButton={selectedButton}
            selectedMeasures={availableSelection}
            paramsList={paramsList}
            isLoading={isLoading}
            error={error || availableSelection.length === 0}
          />
        </Box>
      )}
    </Paper>
  );
};

export default SensorAverageChartPanel;
